import { useTranslations } from 'next-intl'
import { memo } from 'react'
import { Button } from '@/components/button'
import { T } from '@/components/t'
import { useRestaurantDetailContext } from '@/features/restaurants/restaurant-detail/restaurant-detail-context'
import {
    buildRestaurantMapsUrl,
    formatRestaurantAddress,
} from '@/features/restaurants/utils/restaurant-address'
import { SvgOutlinedPlace } from '@/icons/svg-outlined-place'
import { cn } from '@/utils/styles/cn'
import { COLORS } from '@/utils/styles/colors'

interface IRestaurantDetailLocationProps {
    styleType?: 'compact' | 'full'
}

export const RestaurantDetailLocation = memo(
    ({ styleType = 'compact' }: IRestaurantDetailLocationProps) => {
        const { restaurant } = useRestaurantDetailContext()

        const t = useTranslations()

        if (!restaurant || (!restaurant.address && !restaurant.city && !restaurant.zipCode))
            return null

        const fullAddress = formatRestaurantAddress(restaurant)
        const mapsUrl = buildRestaurantMapsUrl(restaurant)

        const content =
            styleType === 'full' ? (
                <p className="text-p-sm md:text-p-md">{fullAddress}</p>
            ) : (
                <div className="flex flex-row items-center justify-start w-full gap-x-2 md:gap-x-1.5 overflow-hidden">
                    <div className="md:block hidden">
                        <SvgOutlinedPlace
                            color="black"
                            size={19}
                            style={{ position: 'relative', bottom: '0.5px' }}
                        />
                    </div>

                    {restaurant.zipCode && (
                        <p className="font-semibold hidden md:block text-[15px] md:text-[16px] whitespace-nowrap">
                            {restaurant.zipCode} {restaurant.city?.name}
                        </p>
                    )}

                    {restaurant.address && (
                        <p
                            className="font-medium md:font-normal text-[15px] md:text-[16px] text-ellipsis overflow-hidden
                                whitespace-nowrap relative group-hover:underline underline-offset-4"
                        >
                            {restaurant.address}
                        </p>
                    )}
                </div>
            )

        const className = cn(
            'flex flex-row items-center justify-between',
            styleType === 'full' ? 'w-fit max-w-full' : 'w-full md:w-fit'
        )

        const linkedContent = !mapsUrl ? (
            <div className={className}>{content}</div>
        ) : (
            <a
                href={mapsUrl}
                target="_blank"
                rel="noreferrer"
                aria-label={t('restaurants.detail.location.openInMaps', { address: fullAddress })}
                className={cn(className, 'group')}
            >
                {content}
            </a>
        )

        if (styleType !== 'full') return linkedContent

        // The address is plain text here, opening maps belongs to the button alone
        return (
            <div className="flex flex-col items-start gap-y-4">
                <div className="flex flex-col gap-y-1">
                    <div className={className}>{content}</div>

                    {restaurant.neighborhood && (
                        <p className="text-p-sm text-fg-tertiary">{restaurant.neighborhood}</p>
                    )}
                </div>

                {mapsUrl && (
                    <Button
                        href={{ externalUrl: mapsUrl }}
                        styleType="secondary"
                        className="gap-x-2"
                    >
                        <SvgOutlinedPlace color={COLORS.fg.primary} size={20} />

                        <T className="text-label-md" t="restaurants.detail.location.mapsButton" />
                    </Button>
                )}
            </div>
        )
    }
)

RestaurantDetailLocation.displayName = 'RestaurantDetailLocation'
